import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { fmtMoney, fmtNum } from "@/lib/format";
import ProductThumb from "@/components/ProductThumb";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Search, Boxes, ListOrdered, Ship, ClipboardList, Loader2, X } from "lucide-react";

function Section({ icon: Icon, title, accent, children }) {
  return (
    <div className="rounded-xl border border-[#1e1e1e] bg-[#0f0f0f] overflow-hidden">
      <div className="px-4 py-3 border-b border-[#1a1a1a] flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <h4 className="font-head font-bold text-white text-sm">{title}</h4>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

const poStatusColor = (s) => (s === "Received" ? "#10B981" : s === "Partially Received" ? "#F59E0B" : "#007AFF");

export default function ProductSearch() {
  const { currency } = useCurrency();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 1) { setResults([]); return; }
      try {
        const r = await api.get(`/products/search?q=${encodeURIComponent(q.trim())}`);
        setResults(r.data.results);
        setOpen(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const openProduct = useCallback(async (sku) => {
    setSelected(sku);
    setOpen(false);
    setQ("");
    setLoadingDetail(true);
    setDetail(null);
    try {
      const r = await api.get(`/products/${encodeURIComponent(sku)}/detail?currency=${currency}`);
      setDetail(r.data);
    } catch { setDetail(null); }
    finally { setLoadingDetail(false); }
  }, [currency]);

  return (
    <>
      <div ref={boxRef} className="relative hidden md:block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666] pointer-events-none" />
        <Input
          data-testid="global-product-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          placeholder="Search any SKU or product…"
          className="h-9 w-56 lg:w-72 pl-9 bg-[#111] border-[#262626] text-white rounded-full text-sm focus-visible:ring-[#007AFF]"
        />
        {open && results.length > 0 && (
          <div data-testid="product-search-results" className="absolute z-50 mt-2 w-[22rem] right-0 max-h-[60vh] overflow-y-auto rounded-xl border border-[#262626] bg-[#0d0d0d] shadow-2xl p-1.5">
            {results.map((r) => (
              <button
                key={r.sku}
                data-testid={`search-result-${r.sku}`}
                onClick={() => openProduct(r.sku)}
                className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-white/5 text-left transition-colors"
              >
                <ProductThumb src={r.image} size={34} title={r.name} />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm truncate">{r.name || r.sku}</p>
                  <p className="text-[#666] text-xs tnum truncate">{r.sku}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[#4da3ff] bg-[#007AFF]/10 px-2 py-0.5 rounded-full shrink-0">{r.type}</span>
              </button>
            ))}
          </div>
        )}
        {open && q.trim() && results.length === 0 && (
          <div className="absolute z-50 mt-2 w-[22rem] right-0 rounded-xl border border-[#262626] bg-[#0d0d0d] shadow-2xl px-4 py-6 text-center text-sm text-[#666]">
            No products match “{q}”.
          </div>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <SheetContent side="right" className="bg-[#0a0a0a] border-l border-[#1e1e1e] text-white w-full sm:max-w-xl overflow-y-auto p-0" data-testid="product-detail-drawer">
          {loadingDetail || !detail ? (
            <div className="flex items-center justify-center h-64 text-[#666]">
              <SheetHeader className="sr-only"><SheetTitle>Product details</SheetTitle><SheetDescription>Loading product details</SheetDescription></SheetHeader>
              {loadingDetail ? <Loader2 className="h-6 w-6 animate-spin" /> : "No data"}
            </div>
          ) : (
            <div>
              <SheetHeader className="p-5 border-b border-[#1a1a1a] space-y-0">
                <div className="flex items-center gap-4">
                  <ProductThumb src={detail.image} size={64} title={detail.name} />
                  <div className="min-w-0">
                    <SheetTitle className="text-white font-head text-lg truncate">{detail.name}</SheetTitle>
                    <SheetDescription className="text-[#A3A3A3] tnum text-xs">
                      {detail.sku}{detail.asin ? ` · ASIN ${detail.asin}` : ""}{detail.is_warehouse ? " · Warehouse SKU" : ""}
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <div className="p-5 space-y-4">
                {/* KPI strip */}
                <div className="grid grid-cols-3 gap-3" data-testid="drawer-kpis">
                  <div className="rounded-xl bg-[#0f0f0f] border border-[#1a1a1a] p-3">
                    <p className="text-[10px] uppercase tracking-widest text-[#666]">Units Sold</p>
                    <p className="text-lg font-bold text-white tnum">{fmtNum(detail.sales.units)}</p>
                  </div>
                  <div className="rounded-xl bg-[#0f0f0f] border border-[#1a1a1a] p-3">
                    <p className="text-[10px] uppercase tracking-widest text-[#666]">Revenue</p>
                    <p className="text-lg font-bold text-[#10B981] tnum">{fmtMoney(detail.sales.revenue, currency, true)}</p>
                  </div>
                  <div className="rounded-xl bg-[#0f0f0f] border border-[#1a1a1a] p-3">
                    <p className="text-[10px] uppercase tracking-widest text-[#666]">Orders</p>
                    <p className="text-lg font-bold text-white tnum">{fmtNum(detail.sales.orders)}</p>
                  </div>
                </div>

                {/* Warehouse stock */}
                <Section icon={Boxes} title="Warehouse Stock" accent="#10B981">
                  {detail.warehouse.length ? detail.warehouse.map((w) => (
                    <div key={w.warehouse_sku} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="text-[#A3A3A3] tnum text-xs">{w.warehouse_sku}</span>
                      <span className="flex items-center gap-4 tnum">
                        <span className="text-[#666] text-xs">start {fmtNum(w.stock_level)}</span>
                        <span className="text-[#666] text-xs">shipped {fmtNum(w.shipped_out)}</span>
                        <span className="font-bold" style={{ color: w.available <= 0 ? "#EF4444" : w.available <= 20 ? "#F59E0B" : "#10B981" }}>avail {fmtNum(w.available)}</span>
                      </span>
                    </div>
                  )) : <p className="text-[#666] text-xs">Not linked to any warehouse SKU.</p>}
                </Section>

                {/* Open POs */}
                <Section icon={ClipboardList} title="Purchase Orders" accent="#007AFF">
                  {detail.purchase_orders.length ? detail.purchase_orders.map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="text-white tnum text-xs">{p.po_number}</span>
                      <span className="flex items-center gap-3 tnum text-xs">
                        <span className="text-[#666]">{fmtNum(p.received)}/{fmtNum(p.ordered)} recv</span>
                        <span className="font-semibold" style={{ color: poStatusColor(p.status) }}>{p.status}</span>
                      </span>
                    </div>
                  )) : <p className="text-[#666] text-xs">No purchase orders.</p>}
                </Section>

                {/* Recent shipments */}
                <Section icon={Ship} title={`FBA Shipments · ${fmtNum(detail.shipments.units)} units`} accent="#8B5CF6">
                  {detail.shipments.recent.length ? detail.shipments.recent.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="text-[#A3A3A3] tnum text-xs">{s.shipment_id}</span>
                      <span className="flex items-center gap-3 tnum text-xs">
                        <span className="text-[#666]">{s.date}</span>
                        <span className="text-white font-semibold">{fmtNum(s.quantity)}</span>
                      </span>
                    </div>
                  )) : <p className="text-[#666] text-xs">No shipments.</p>}
                </Section>

                {/* Recent orders */}
                <Section icon={ListOrdered} title="Recent Orders" accent="#F59E0B">
                  {detail.sales.recent.length ? detail.sales.recent.map((o, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="text-[#A3A3A3] tnum text-xs truncate max-w-[140px]">{o.order_id}</span>
                      <span className="flex items-center gap-3 tnum text-xs">
                        <span className="text-[#666]">{o.date}</span>
                        <span className="text-[#666]">×{fmtNum(o.quantity)}</span>
                        <span className="text-white font-semibold">{fmtMoney(o.revenue, currency)}</span>
                      </span>
                    </div>
                  )) : <p className="text-[#666] text-xs">No orders.</p>}
                </Section>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
